import { env } from "@/config/env";
import { buildQueryParams, type ListQueryParams } from "@/lib/api/query-params";
import {
  isApiError,
  type ApiListResult,
  type ApiResult,
  type ErrorEnvelope,
  type ListEnvelope,
  type SuccessEnvelope,
} from "@/lib/api/types";
import { getStoredToken } from "@/lib/auth/token-storage";

const API_BASE_URL: string = env.VITE_API_BASE_URL;

/**
 * Thrown for every non-2xx / envelope-error response. Callers should branch
 * on `code`, never on `message` — messages are for humans, not logic.
 */
export class ApiClientError extends Error {
  readonly code: ErrorEnvelope["error"]["code"];
  readonly details: ErrorEnvelope["error"]["details"];
  readonly retryable: boolean;
  readonly requestId: string;
  /** Populated from `Retry-After` when the server sent one on a 429. */
  readonly retryAfterMs?: number;

  constructor(envelope: ErrorEnvelope, retryAfterMs?: number) {
    super(envelope.error.message);
    this.name = "ApiClientError";
    this.code = envelope.error.code;
    this.details = envelope.error.details;
    this.retryable = envelope.error.retryable;
    this.requestId = envelope.meta.requestId;
    this.retryAfterMs = retryAfterMs;
  }
}

export function hasApiErrorDetail(error: unknown, code: string): boolean {
  return (
    error instanceof ApiClientError &&
    error.details?.some((detail) => detail.code === code) === true
  );
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (!Number.isNaN(seconds)) return seconds * 1000;
  const dateMs = Date.parse(header);
  if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now());
  return undefined;
}

function isConformingErrorEnvelope(body: unknown): body is ErrorEnvelope {
  return (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    (body as { error?: unknown }).error !== undefined
  );
}

/**
 * Builds the `ErrorEnvelope` to throw for a non-2xx response. `response.ok`
 * (the HTTP status) is authoritative here, independent of body shape — a
 * route with no matching FastAPI handler returns `{"detail": "Not Found"}`
 * (no `error` key), which previously slipped past an `envelope.error !==
 * undefined` check and got treated as a success envelope with no `data`,
 * crashing every caller that read `.data.length` off it.
 */
function buildErrorEnvelope(body: unknown, status: number): ErrorEnvelope {
  if (isConformingErrorEnvelope(body)) return body;
  return {
    error: {
      code: "INTERNAL_ERROR",
      message: `Request failed with status ${status}`,
      retryable: false,
    },
    meta: { requestId: "", at: new Date().toISOString() },
  };
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  params?: URLSearchParams;
  /**
   * Required for mutating requests. The caller owns generating this exactly
   * once per user intent (e.g. per button click), not per network attempt.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
  formData?: FormData;
}

async function request<TEnvelope extends { error?: unknown }>(
  path: string,
  options: RequestOptions = {},
): Promise<TEnvelope> {
  const url = new URL(path.replace(/^\//, ""), ensureTrailingSlash(API_BASE_URL));
  if (options.params) {
    for (const [key, value] of options.params) url.searchParams.set(key, value);
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
  // Attach the real session token whenever one is stored, in every mode
  // (dev and prod alike) — there is no separate "dev auth" path anymore.
  const token = getStoredToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
    body:
      options.formData ??
      (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    signal: options.signal,
  });

  // A 204 (e.g. DELETE) has no body at all — calling response.json() on an
  // empty body throws, so treat "no content" as an empty success envelope
  // rather than a parse error.
  if (response.status === 204) {
    return { data: null, meta: { requestId: "", at: "" } } as unknown as TEnvelope;
  }

  const body: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const retryAfterMs =
      response.status === 429
        ? parseRetryAfter(response.headers.get("Retry-After"))
        : undefined;
    throw new ApiClientError(buildErrorEnvelope(body, response.status), retryAfterMs);
  }

  return body as TEnvelope;
}

function ensureTrailingSlash(base: string): string {
  if (!base) return window.location.origin + "/";
  return base.endsWith("/") ? base : `${base}/`;
}

/** GET a single resource. */
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const envelope = await request<SuccessEnvelope<T>>(path, { method: "GET", signal });
  return envelope.data;
}

/** GET a paginated list resource, returning data alongside pagination meta. */
export async function apiGetList<T>(
  path: string,
  params: ListQueryParams = {},
  signal?: AbortSignal,
): Promise<ListEnvelope<T>> {
  return request<ListEnvelope<T>>(path, {
    method: "GET",
    params: buildQueryParams(params),
    signal,
  });
}

interface MutateOptions {
  body?: unknown;
  /** Required — generate once per user intent, reuse across retries of that intent. */
  idempotencyKey: string;
  signal?: AbortSignal;
}

/** POST/PATCH/PUT/DELETE a resource. All mutating calls require an idempotency key. */
export async function apiMutate<T>(
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  options: MutateOptions,
): Promise<T> {
  const envelope = await request<SuccessEnvelope<T>>(path, {
    method,
    body: options.body,
    idempotencyKey: options.idempotencyKey,
    signal: options.signal,
  });
  return envelope.data;
}

/** Multipart mutation. The browser sets the boundary; do not set Content-Type manually. */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  idempotencyKey: string,
): Promise<T> {
  const envelope = await request<SuccessEnvelope<T>>(path, {
    method: "POST",
    formData,
    idempotencyKey,
  });
  return envelope.data;
}

export type { ApiListResult, ApiResult };
export { isApiError };
